import { useState, useEffect, useCallback } from "react";
import channelService from "../services/channelService";
import toast from "react-hot-toast";

export const useChannels = () => {
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchChannels = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await channelService.getChannels();
      setChannels(response.data);
    } catch (err) {
      setError(err.message);
      toast.error("Failed to load channels");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChannels();
  }, []);

  const refetch = () => {
    fetchChannels();
  };

  return {
    channels,
    isLoading,
    error,
    refetch,
  };
};

export const useCreateChannel = () => {
  const [formData, setFormData] = useState({
    name: "",
    type: "whatsapp", // Default to whatsapp
    agentId: "", // Changed from agent_name to agentId
    config: {
      business_name: "",
      welcome_message: "Hello! How can we help you today?",
      business_hours: "08:00-22:00 WIB",
    },
  });

  const updateFormData = useCallback((updates) => {
    setFormData((prev) => {
      // Handle nested config updates
      if (updates.config) {
        return {
          ...prev,
          config: {
            ...prev.config,
            ...updates.config,
          },
        };
      }
      return { ...prev, ...updates };
    });
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      type: "whatsapp",
      agentId: "",
      config: {
        business_name: "",
        welcome_message: "Hello! How can we help you today?",
        business_hours: "08:00-22:00 WIB",
      },
    });
  }, []);

  const createChannel = async () => {
    try {
      const response = await channelService.createChannel(formData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  return {
    formData,
    updateFormData,
    resetForm,
    createChannel,
  };
};

// Helper function for delay
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const useChannelDetail = (channelId) => {
  const [channel, setChannel] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const fetchChannelDetail = async (id) => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await channelService.getChannelById(id);
      setChannel(response.data.channel);
      setStats(response.data.channel.stats || null);
    } catch (err) {
      setError(err.message);
      console.error("Failed to load channel details:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadChannel = async () => {
      if (channelId && isMounted) {
        await fetchChannelDetail(channelId);
      }
    };

    // Reset state when channelId changes
    setChannel(null);
    setStats(null);
    setError(null);

    loadChannel();

    return () => {
      isMounted = false;
    };
  }, [channelId]);

  const updateStatus = async (status) => {
    if (!channelId) return;

    setIsUpdating(true);
    try {
      await channelService.updateChannelStatus(channelId, status);
      toast.success(`Channel ${status} successfully`);
      await fetchChannelDetail(channelId); // Refresh data
    } catch (error) {
      toast.error("Failed to update channel status");
      console.error("Update status error:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteChannel = async () => {
    if (!channelId) return;

    setIsDeleting(true);
    try {
      await channelService.deleteChannel(channelId);
      toast.success("Channel deleted successfully");
      return true;
    } catch (error) {
      toast.error("Failed to delete channel");
      console.error("Delete channel error:", error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  const updateChannelConfig = async (configData) => {
    if (!channelId) return;

    setIsUpdatingConfig(true);
    try {
      await channelService.updateChannelConfig(channelId, configData);
      // Remove toast from here - let calling component handle notifications
      await fetchChannelDetail(channelId); // Refresh data
      return true;
    } catch (error) {
      // Keep error toast here as it's important for debugging
      toast.error("Failed to update channel configuration");
      console.error("Update config error:", error);
      return false;
    } finally {
      setIsUpdatingConfig(false);
    }
  };

  const retryWhatsAppSetup = async () => {
    if (!channelId) return;

    setIsRetrying(true);
    try {
      await channelService.retryWhatsAppSetup(channelId);
      toast.success("WhatsApp reconnection initiated successfully");
      await fetchChannelDetail(channelId); // Refresh data
      return true;
    } catch (error) {
      toast.error("Failed to retry WhatsApp setup");
      console.error("Retry WhatsApp error:", error);
      return false;
    } finally {
      setIsRetrying(false);
    }
  };

  const refetch = () => {
    if (channelId) {
      fetchChannelDetail(channelId);
    }
  };

  return {
    channel,
    stats,
    isLoading,
    error,
    refetch,
    updateStatus,
    isUpdating,
    deleteChannel,
    isDeleting,
    updateChannelConfig,
    isUpdatingConfig,
    retryWhatsAppSetup,
    isRetrying,
  };
};
